import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  GripVertical, Eye, EyeOff, Save, RotateCcw, ShieldCheck, ArrowLeft, Sliders,
  Users as UsersIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NAV_CATALOG } from "@/lib/navCatalog";

/**
 * Full-control sidebar customization page.
 * - Drag-and-drop to reorder (within each group).
 * - Eye toggle to hide/show any tab.
 * - Reset button clears the per-user override.
 * - Admins can additionally save the current layout as the TENANT DEFAULT
 *   (`users.manage` perm) — every user in the tenant who hasn't customized
 *   will pick it up on next page load.
 */
export default function SidebarSettings() {
  const nav = useNavigate();
  const { user, hasPerm, isSuperAdmin } = useAuth();
  const canManageTenant = hasPerm("users.manage");

  const [layout, setLayout] = useState([]); // [{key, hidden, group, ...catalog}]
  const [source, setSource] = useState("default");
  const [saving, setSaving] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [savingTenant, setSavingTenant] = useState(false);

  const visibleCatalog = useMemo(
    () => NAV_CATALOG.filter((n) => {
      if (n.perm && !hasPerm(n.perm)) return false;
      if (n.group === "platform" && !isSuperAdmin()) return false;
      return true;
    }),
    [hasPerm, isSuperAdmin],
  );

  const seedFromPrefs = (prefsLayout) => {
    const orderMap = new Map((prefsLayout || []).map((p, i) => [p.key, i]));
    const hiddenMap = new Map((prefsLayout || []).map((p) => [p.key, !!p.hidden]));
    const withOrder = visibleCatalog.map((n) => ({
      ...n,
      hidden: !!hiddenMap.get(n.key),
      _ord: orderMap.has(n.key) ? orderMap.get(n.key) : Number.MAX_SAFE_INTEGER,
    }));
    withOrder.sort((a, b) => a._ord - b._ord);
    setLayout(withOrder);
  };

  useEffect(() => {
    api.get("/sidebar/prefs")
      .then((r) => { seedFromPrefs(r.data.layout || []); setSource(r.data.source || "default"); })
      .catch(() => seedFromPrefs([]));
  }, [user?.id]);

  const grouped = useMemo(() => {
    const g = { main: [], admin: [], platform: [] };
    layout.forEach((row) => { if (g[row.group]) g[row.group].push(row); });
    return g;
  }, [layout]);

  const doDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch { /* noop */ }
  };
  const doDrop = (targetKey) => () => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); return; }
    const from = layout.findIndex((x) => x.key === dragKey);
    const to = layout.findIndex((x) => x.key === targetKey);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    if (layout[from].group !== layout[to].group) { setDragKey(null); return; }
    const next = [...layout];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setLayout(next);
    setDragKey(null);
  };
  const toggleHidden = (key) => {
    setLayout(layout.map((x) => x.key === key ? { ...x, hidden: !x.hidden } : x));
  };

  const savePayload = () => layout.map(({ key, hidden }) => ({ key, hidden: !!hidden }));

  const saveUser = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/sidebar/prefs", { layout: savePayload() });
      seedFromPrefs(data.layout);
      setSource("user");
      toast.success("Your sidebar layout is saved. Reload any open tab to see it applied.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetUser = async () => {
    if (!confirm("Reset your sidebar to the tenant default? Your custom order will be cleared.")) return;
    try {
      const { data } = await api.post("/sidebar/prefs/reset");
      seedFromPrefs(data.layout || []);
      setSource(data.source || "default");
      toast.success("Reset to tenant default");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const saveAsTenantDefault = async () => {
    if (!canManageTenant) return;
    if (!confirm("Save the current layout as the TENANT DEFAULT?\n\nEvery user in your tenant who hasn't customized their own sidebar will pick this up on their next load. Users who've already customized their sidebar keep their overrides.")) return;
    try {
      setSavingTenant(true);
      await api.put("/sidebar/prefs/tenant", { layout: savePayload() });
      toast.success("Saved as tenant default — new users will see this layout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingTenant(false); }
  };

  return (
    <div className="p-8 max-w-[900px] mx-auto" data-testid="sidebar-settings">
      <header className="mb-6">
        <button onClick={() => nav(-1)} className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white mb-3 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <Sliders className="w-3.5 h-3.5 text-[#00F0FF]" /> Sidebar customization
        </div>
        <h1 className="font-display font-black text-5xl mt-2">Customize Sidebar</h1>
        <p className="text-[#8E9196] mt-1 text-sm max-w-2xl">
          Drag any tab to reorder it, or click the eye to hide the ones you never use.
          Changes save to your account and follow you across devices.
          Reordering only works <span className="text-white">within the same section</span> (Main / Admin / Platform).
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-widest"
             style={{ borderColor: source === "user" ? "#00F0FF60" : "#52525B60", color: source === "user" ? "#00F0FF" : "#8E9196" }}
             data-testid="sidebar-source-badge">
          Current source: {source}
        </div>
      </header>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={saveUser} disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black disabled:opacity-40 px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
          <Save className="w-3.5 h-3.5" /> Save my layout
        </button>
        <button onClick={resetUser}
          data-testid="settings-reset"
          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
          <RotateCcw className="w-3.5 h-3.5" /> Reset to tenant default
        </button>
        {canManageTenant && (
          <button onClick={saveAsTenantDefault} disabled={savingTenant}
            data-testid="settings-save-tenant"
            title="Save this as the sidebar default for everyone in your tenant"
            className="inline-flex items-center gap-1.5 border border-[#B57BFF]/60 hover:border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black disabled:opacity-40 px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
            <UsersIcon className="w-3.5 h-3.5" /> Save as tenant default
          </button>
        )}
      </div>

      <div className="space-y-6">
        <GroupSection
          label="Main"
          rows={grouped.main}
          onDragStart={doDragStart}
          onDrop={doDrop}
          onToggleHidden={toggleHidden}
          dragKey={dragKey}
        />
        {grouped.admin.length > 0 && (
          <GroupSection
            label="Admin"
            rows={grouped.admin}
            onDragStart={doDragStart}
            onDrop={doDrop}
            onToggleHidden={toggleHidden}
            dragKey={dragKey}
          />
        )}
        {grouped.platform.length > 0 && (
          <GroupSection
            label="Platform"
            rows={grouped.platform}
            onDragStart={doDragStart}
            onDrop={doDrop}
            onToggleHidden={toggleHidden}
            dragKey={dragKey}
            accent="#B57BFF"
          />
        )}
      </div>

      <div className="mt-8 border border-white/10 rounded-sm p-4 bg-[#0a0a0a] flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
        <div className="text-xs text-[#8E9196]">
          <div className="text-white mb-1">Precedence</div>
          Your saved layout beats the tenant default. The tenant default beats the factory order.
          Newly-released tabs auto-appear at the end of their section so you never miss a feature.
        </div>
      </div>
    </div>
  );
}

function GroupSection({ label, rows, onDragStart, onDrop, onToggleHidden, dragKey, accent = "#00F0FF" }) {
  return (
    <section>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: accent }}>
        {label} · {rows.length} tab{rows.length === 1 ? "" : "s"}
      </div>
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] divide-y divide-white/5">
        {rows.map((row) => (
          <div key={row.key}
            data-testid={`settings-row-${row.key}`}
            draggable
            onDragStart={onDragStart(row.key)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop(row.key)}
            className={`flex items-center gap-3 px-3 py-2 cursor-grab active:cursor-grabbing transition-all ${
              dragKey === row.key ? "opacity-40 bg-[#00F0FF]/[0.04]" : "hover:bg-white/[0.02]"
            } ${row.hidden ? "opacity-60" : ""}`}
          >
            <GripVertical className="w-4 h-4 text-[#52525B]" />
            <row.icon className="w-4 h-4" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-sm text-white">{row.label}</div>
              <div className="text-[10px] font-mono text-[#52525B]">{row.to}</div>
            </div>
            {row.badge && (
              <span className="font-mono text-[9px] tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1">
                {row.badge}
              </span>
            )}
            <button type="button" onClick={() => onToggleHidden(row.key)}
              data-testid={`settings-toggle-${row.key}`}
              title={row.hidden ? "Show this tab" : "Hide this tab"}
              className="p-1.5 text-[#8E9196] hover:text-white">
              {row.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-3 py-4 text-xs text-[#52525B] text-center">
            No tabs available in this section for your role.
          </div>
        )}
      </div>
    </section>
  );
}
